/**
 * Integrations — testops-web's mirror of marketing's /integrations page.
 *
 * Same MCP gateway backend, same UI body. Differences vs marketing's version:
 *   - No top navbar (testops-web's Layout provides nav + user menu)
 *   - No logout modal (Layout owns the logout flow)
 *   - Wrapped in a scrollable container so the page fits inside Layout's
 *     viewport-locked main area.
 */
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Plug,
  Plus,
  Settings,
  Trash2,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink,
  Search,
  Filter,
} from 'lucide-react'
import { RefreshCw } from 'lucide-react'
import { fetchMCPServers, fetchMCPConnections, deleteMCPConnection, startOAuthConnect, type MCPServer, type MCPConnection } from '../services/mcp'
import { ConnectionModal } from '../components/ConnectionModal'
import { RequestIntegrationModal } from '../components/RequestIntegrationModal'
import { ConfirmModal } from '../components/ConfirmModal'
import { useAuthStore } from '../stores/authStore'

const categoryIcons: Record<string, string> = {
  'project-management': '📋',
  'database': '🗄️',
  'communication': '💬',
  'ci-cd': '🔄',
  'cloud': '☁️',
  'version-control': '🔀',
  'monitoring': '📊',
  'security': '🔒',
}

const categoryNames: Record<string, string> = {
  'project-management': 'Project Management',
  'database': 'Database',
  'communication': 'Communication',
  'ci-cd': 'CI/CD',
  'cloud': 'Cloud',
  'version-control': 'Version Control',
  'monitoring': 'Monitoring',
  'security': 'Security',
}

export function Integrations() {
  const { user, tenantSlug } = useAuthStore()
  const [servers, setServers] = useState<MCPServer[]>([])
  const [connections, setConnections] = useState<MCPConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showConnectionModal, setShowConnectionModal] = useState(false)
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [selectedServer, setSelectedServer] = useState<MCPServer | null>(null)
  const [editingConnection, setEditingConnection] = useState<MCPConnection | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [connectionToDelete, setConnectionToDelete] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Result banner after returning from an OAuth "Connect with ..." round-trip
  const [oauthResult, setOauthResult] = useState<{ success: boolean; message: string } | null>(null)

  useEffect(() => {
    // The OAuth callback redirects back here with the outcome in the query
    // string. Consume it once, show a banner, and clean the URL so refresh /
    // bookmark doesn't replay the message.
    const params = new URLSearchParams(window.location.search)
    const connectedId = params.get('integration_connected')
    const oauthError = params.get('integration_error')
    const serverKey = params.get('integration_server')
    const reconnected = params.get('integration_reconnected') === '1'
    if (connectedId || oauthError) {
      setOauthResult(
        connectedId
          ? {
              success: true,
              message: `${serverKey ? categoryTitle(serverKey) : 'Integration'} ${reconnected ? 'reconnected' : 'connected'} successfully`,
            }
          : { success: false, message: oauthError || 'Connection failed' }
      )
      params.delete('integration_connected')
      params.delete('integration_error')
      params.delete('integration_server')
      params.delete('integration_reconnected')
      const rest = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (rest ? `?${rest}` : ''))
    }
    loadData()
  }, [])

  function categoryTitle(serverKey: string): string {
    return serverKey.charAt(0).toUpperCase() + serverKey.slice(1)
  }

  async function loadData() {
    try {
      setLoading(true)
      const serversData = await fetchMCPServers()
      setServers(serversData)

      const token = localStorage.getItem('token') || localStorage.getItem('access_token')
      if (token && token !== 'null') {
        try {
          const connectionsData = await fetchMCPConnections()
          setConnections(connectionsData)
        } catch {
          setConnections([])
        }
      } else {
        setConnections([])
      }
    } catch {
      // Surfaced via empty state below if servers list never populates
    } finally {
      setLoading(false)
    }
  }

  function handleDeleteConnection(connectionId: string) {
    setConnectionToDelete(connectionId)
    setShowDeleteConfirm(true)
  }

  async function confirmDelete() {
    if (!connectionToDelete) return
    try {
      await deleteMCPConnection(connectionToDelete)
      await loadData()
      setShowDeleteConfirm(false)
      setConnectionToDelete(null)
      setDeleteError(null)
    } catch {
      setDeleteError('Failed to delete connection. Please try again.')
      setShowDeleteConfirm(false)
    }
  }

  async function handleReconnect(connection: MCPConnection) {
    try {
      const authorizationUrl = await startOAuthConnect(
        connection.server_key,
        `${window.location.origin}/app/integrations`,
        { reconnectConnectionId: connection.id }
      )
      window.location.href = authorizationUrl
    } catch (err: any) {
      setOauthResult({ success: false, message: err.message || 'Could not start reconnect' })
    }
  }

  function handleAddConnection(server: MCPServer) {
    setSelectedServer(server)
    setEditingConnection(null)
    setShowConnectionModal(true)
  }

  function handleEditConnection(connection: MCPConnection) {
    const server = servers.find((s) => s.server_key === connection.server_key)
    if (server) {
      setSelectedServer(server)
      setEditingConnection(connection)
      setShowConnectionModal(true)
    }
  }

  const filteredServers = servers.filter((server) => {
    const matchesCategory = !selectedCategory || server.category === selectedCategory
    const matchesSearch =
      !searchQuery ||
      server.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      server.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesCategory && matchesSearch && server.is_active
  })

  const categories = Array.from(new Set(servers.map((s) => s.category)))

  const connectionCounts = connections.reduce((acc, conn) => {
    acc[conn.server_key] = (acc[conn.server_key] || 0) + 1
    return acc
  }, {} as Record<string, number>)

  if (loading) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-foreground/[0.04] to-foreground/[0.06]">
        <Loader2 className="w-8 h-8 animate-spin text-success-foreground" />
      </div>
    )
  }

  return (
    <div className="absolute inset-0 overflow-y-auto bg-gradient-to-br from-foreground/[0.04] to-foreground/[0.06]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* OAuth round-trip result banner */}
        {oauthResult && (
          <div
            className={`mb-6 flex items-center justify-between gap-3 p-4 rounded-lg border ${
              oauthResult.success
                ? 'bg-success-soft border-success/30'
                : 'bg-destructive-soft border-destructive/30'
            }`}
          >
            <div className="flex items-center gap-2">
              {oauthResult.success ? (
                <CheckCircle className="w-5 h-5 text-success-foreground flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-destructive-foreground flex-shrink-0" />
              )}
              <p className={`text-sm font-medium ${oauthResult.success ? 'text-success-foreground' : 'text-destructive-foreground'}`}>
                {oauthResult.message}
              </p>
            </div>
            <button
              onClick={() => setOauthResult(null)}
              className={`text-xs font-medium ${oauthResult.success ? 'text-success-foreground' : 'text-destructive-foreground'} hover:underline`}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-success-soft rounded-xl">
                <Plug className="w-8 h-8 text-success-foreground" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground">Integrations</h1>
                <p className="text-muted-foreground mt-1">
                  Connect TestOps Command with your favorite tools and services
                </p>
              </div>
            </div>
            <a
              href="https://modelcontextprotocol.io/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-success-foreground hover:text-success-foreground bg-success-soft hover:bg-success-soft rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              MCP Documentation
            </a>
          </div>

          {/* Request New Integration */}
          <div className="mt-4 flex justify-center">
            <button
              onClick={() => setShowRequestModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04] border border-strong rounded-lg transition"
            >
              <Plus className="w-4 h-4" />
              Request a new integration
            </button>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-card rounded-full shadow-sm border border-strong">
              <span className="text-lg font-bold text-foreground">{servers.filter((s) => s.is_active).length}</span>
              <span className="text-xs text-muted-foreground">Available</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-card rounded-full shadow-sm border border-strong">
              <span className="text-lg font-bold text-success-foreground">{connections.length}</span>
              <span className="text-xs text-muted-foreground">Connected</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-card rounded-full shadow-sm border border-strong">
              <span className="text-lg font-bold text-foreground">{categories.length}</span>
              <span className="text-xs text-muted-foreground">Categories</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg shadow-sm border border-strong p-4 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-subtle-foreground" />
              <input
                type="text"
                placeholder="Search integrations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-strong bg-card text-foreground placeholder:text-subtle-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-subtle-foreground" />
              <select
                value={selectedCategory || ''}
                onChange={(e) => setSelectedCategory(e.target.value || null)}
                className="px-4 py-2 border border-strong bg-card text-foreground rounded-lg focus:ring-2 focus:ring-success focus:border-success"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {categoryNames[category] ||
                      category
                        .split('-')
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredServers.map((server) => (
            <IntegrationCard
              key={server.id}
              server={server}
              connectionCount={connectionCounts[server.server_key] || 0}
              onAddConnection={() => handleAddConnection(server)}
            />
          ))}
        </div>

        {filteredServers.length === 0 && (
          <div className="text-center py-12">
            <Plug className="w-16 h-16 text-subtle-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No integrations found</h3>
            <p className="text-muted-foreground">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* My Connections */}
        {connections.length > 0 && (
          <div className="mt-12">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground mb-6">My Connections</h2>
            <div className="bg-card rounded-lg shadow-sm border border-strong divide-y divide-strong">
              {connections.map((connection) => (
                <ConnectionRow
                  key={connection.id}
                  connection={connection}
                  server={servers.find((s) => s.server_key === connection.server_key)}
                  onEdit={() => handleEditConnection(connection)}
                  onDelete={() => handleDeleteConnection(connection.id)}
                  onReconnect={() => handleReconnect(connection)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Connection Modal */}
      <ConnectionModal
        isOpen={showConnectionModal}
        onClose={() => {
          setShowConnectionModal(false)
          setEditingConnection(null)
        }}
        server={selectedServer}
        connection={editingConnection}
        onSuccess={loadData}
      />

      {/* Request Integration Modal */}
      <RequestIntegrationModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        prefillName={user?.full_name || ''}
        prefillEmail={user?.email || ''}
        prefillCompany={tenantSlug || ''}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false)
          setConnectionToDelete(null)
        }}
        onConfirm={confirmDelete}
        title="Delete Connection?"
        message="Are you sure you want to delete this connection? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Error Toast */}
      {deleteError && (
        <div className="fixed bottom-4 right-4 bg-destructive-soft border border-destructive/30 rounded-lg p-4 shadow-lg max-w-md z-50">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-destructive-foreground" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-destructive-foreground">{deleteError}</p>
            </div>
            <button
              onClick={() => setDeleteError(null)}
              className="flex-shrink-0 text-destructive hover:text-destructive-foreground"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function IntegrationCard({
  server,
  connectionCount,
  onAddConnection,
}: {
  server: MCPServer
  connectionCount: number
  onAddConnection: () => void
}) {
  const categoryIcon = categoryIcons[server.category] || '🔌'

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.01, y: -2 }}
      transition={{ duration: 0.2 }}
      className="bg-card rounded-lg p-3 shadow-sm border border-strong hover:border-success/50 hover:shadow-md"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {server.icon_url ? (
            <div className="w-7 h-7 flex items-center justify-center bg-foreground/[0.04] dark:bg-white rounded flex-shrink-0">
              <img src={server.icon_url} alt={server.name} className="w-5 h-5 object-contain" />
            </div>
          ) : (
            <div className="text-xl flex-shrink-0">{categoryIcon}</div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-sm text-foreground truncate">{server.name}</h3>
            <p className="text-xs text-muted-foreground truncate">{server.supported_tools.length} tools</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {connectionCount > 0 && (
            <span className="px-2 py-0.5 bg-success-soft text-success-foreground text-xs font-medium rounded-full">
              {connectionCount}
            </span>
          )}
          <button
            onClick={onAddConnection}
            className="flex items-center gap-1 px-2 py-1 bg-success text-white text-xs font-medium rounded hover:bg-success/90 transition"
          >
            <Plus className="w-3 h-3" />
            <span>Add</span>
          </button>
        </div>
      </div>
    </motion.div>
  )
}

function ConnectionRow({
  connection,
  server,
  onEdit,
  onDelete,
  onReconnect,
}: {
  connection: MCPConnection
  server?: MCPServer
  onEdit: () => void
  onDelete: () => void
  onReconnect: () => void
}) {
  const isHealthy = connection.status === 'active'
  const isExpired = connection.status === 'expired'
  // Reconnect is meaningful for OAuth connections whenever the provider's
  // OAuth app is configured — expired ones NEED it; healthy ones may want
  // to re-consent (e.g. after a provider-side revocation).
  const canReconnect = connection.auth_type === 'oauth' && Boolean(server?.oauth_enabled)
  const categoryIcon = server ? categoryIcons[server.category] || '🔌' : '🔌'

  return (
    <div className="p-4 hover:bg-foreground/[0.04] transition">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
        <div className="flex items-center space-x-4 flex-1">
          {server?.icon_url ? (
            <div className="w-8 h-8 flex items-center justify-center bg-foreground/[0.04] dark:bg-white rounded">
              <img src={server.icon_url} alt={server.name} className="w-6 h-6 object-contain" />
            </div>
          ) : (
            <div className="text-2xl">{categoryIcon}</div>
          )}
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h4 className="font-medium text-foreground">{connection.connection_name}</h4>
              {isHealthy ? (
                <CheckCircle className="w-4 h-4 text-success" />
              ) : (
                <XCircle className="w-4 h-4 text-destructive" />
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {server?.name} • {connection.usage_count} executions
              {connection.last_used_at &&
                ` • Last used ${new Date(connection.last_used_at).toLocaleDateString()}`}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {isExpired && (
            <span className="px-2 py-1 bg-warning-soft text-warning-foreground text-xs font-medium rounded-full">
              Expired
            </span>
          )}
          {connection.is_shared_with_team && (
            <span className="px-2 py-1 bg-info-soft text-info-foreground text-xs font-medium rounded-full">
              Team
            </span>
          )}
          {canReconnect && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onReconnect()
              }}
              className={
                isExpired
                  ? 'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-success rounded-lg hover:bg-success/90 transition'
                  : 'p-2 text-subtle-foreground hover:text-muted-foreground transition'
              }
              title="Reconnect — sign in at the provider again"
            >
              <RefreshCw className="w-4 h-4" />
              {isExpired && 'Reconnect'}
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit()
            }}
            className="p-2 text-subtle-foreground hover:text-muted-foreground transition"
            title="Settings"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="p-2 text-subtle-foreground hover:text-destructive-foreground transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
